import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ApiError, NetworkError, ValidationError, AuthenticationError } from '@/lib/api-errors';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle, AlertCircle, RotateCcw, Loader2, Clock, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { ProgressSteps } from "@/components/auth/ProgressSteps";
import { supabase } from "@/integrations/supabase/client";
import { CooldownButton } from "@/components/ui/CooldownButton";
import { authService } from "@/services/auth.service";

type VerificationState = 'waiting' | 'loading' | 'success' | 'error' | 'expired' | 'alreadyVerified' | 'invalidToken';

/**
 * Resolves the user's email using a priority chain:
 * 1. URL param (fastest, works on first load from registration)
 * 2. Supabase session (works after reload if session persists)
 * 3. sessionStorage (written during registration, survives reload)
 */
async function resolveEmail(searchParams: URLSearchParams): Promise<string | null> {
  // Priority 1: URL param
  const emailFromUrl = searchParams.get('email');
  if (emailFromUrl) return emailFromUrl;

  // Priority 2: Supabase auth state
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.email) return session.user.email;
  } catch {
    // Session not available, continue to next source
  }

  // Priority 3: sessionStorage (written during registration)
  const emailFromStorage = sessionStorage.getItem('pending_verification_email');
  if (emailFromStorage) return emailFromStorage;

  return null;
}

export default function VerifyEmail() {
  const [verificationStatus, setVerificationStatus] = useState<VerificationState>('waiting');
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [manualEmail, setManualEmail] = useState("");
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resolve email on mount using priority chain
  useEffect(() => {
    const initEmail = async () => {
      const resolved = await resolveEmail(searchParams);
      if (resolved) {
        setEmail(resolved);
        // Persist to sessionStorage for future reloads
        sessionStorage.setItem('pending_verification_email', resolved);
      } else {
        setShowEmailInput(true);
      }
    };
    initEmail();
  }, [searchParams]);

  // Handle click-to-confirm email verification with token_hash
  useEffect(() => {
    const processVerification = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token_hash = urlParams.get('token_hash');
      const type = urlParams.get('type');

      // If user is already authenticated and verified, redirect to dashboard
      if (session?.user?.email_confirmed_at) {
        setVerificationStatus('alreadyVerified');
        sessionStorage.removeItem('pending_verification_email');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
        return;
      }

      // If no token in URL, user is waiting for email (not invalid)
      if (!token_hash || !type) {
        setVerificationStatus('waiting');
        return;
      }

      try {
        setVerificationStatus('loading');
        
        const result = await authService.verifyEmail({
          tokenHash: token_hash,
          type: type as 'email'
        });

        if (result.success === false) {
          Sentry.captureException(new Error(result.error.message), {
            tags: { context: 'auth_error', operation: 'verify_email', component: 'VerifyEmail' },
            extra: { error: result.error }
          });
          
          if (result.error.code === 'TOKEN_EXPIRED') {
            setVerificationStatus('expired');
            setErrorMessage(result.error.userMessage);
          } else if (result.error.code === 'TOKEN_INVALID') {
            setVerificationStatus('invalidToken');
            setErrorMessage(result.error.userMessage);
          } else {
            setVerificationStatus('error');
            setErrorMessage(result.error.userMessage);
          }
          return;
        }

        // Verification successful — clean up sessionStorage
        sessionStorage.removeItem('pending_verification_email');
        setVerificationStatus('success');
        toast({
          title: "✅ You're verified!",
          description: "Let's personalize your experience..."
        });

        const { data: { session: newSession } } = await supabase.auth.getSession();

        setTimeout(() => {
          navigate('/auth/complete-profile');
        }, 2500);
      } catch (err: any) {
        if (err instanceof AuthenticationError) {
          Sentry.captureException(err, {
            tags: { context: 'auth_error', operation: 'verify_email', component: 'VerifyEmail' },
            extra: { message: err.message }
          });
          setVerificationStatus('error');
          setErrorMessage('Authentication failed. Please try logging in again.');
        } else if (err instanceof NetworkError) {
          Sentry.captureException(err, {
            tags: { context: 'network_error', operation: 'verify_email' },
            extra: { message: err.message }
          });
          setVerificationStatus('error');
          setErrorMessage('Connection error. Please check your internet and try again.');
        } else if (err instanceof ValidationError) {
          Sentry.captureException(err, {
            tags: { context: 'validation_error', operation: 'verify_email' },
            extra: { message: err.message }
          });
          setVerificationStatus('invalidToken');
          setErrorMessage('Invalid verification code. Please request a new verification email.');
        } else {
          Sentry.captureException(err, {
            tags: { context: 'auth_error', operation: 'verify_email', component: 'VerifyEmail' },
            extra: { message: 'Unexpected verification error' }
          });
          setVerificationStatus('error');
          setErrorMessage('An unexpected error occurred during verification. Please try again.');
        }
      }
    };

    processVerification();
  }, [searchParams, session, navigate]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
        sessionStorage.removeItem('pending_verification_email');
        setVerificationStatus('success');
        toast({
          title: "Email verified",
          description: "Your email has been verified successfully. Redirecting to profile setup..."
        });
        setTimeout(() => navigate('/auth/complete-profile'), 2000);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleResendVerification = async () => {
    const resendEmail = email || manualEmail;
    
    if (!resendEmail) {
      setShowEmailInput(true);
      toast({
        title: "Email required",
        description: "Please enter your email address to resend verification.",
        variant: "destructive"
      });
      return;
    }

    // If using manual email, persist it
    if (manualEmail && !email) {
      setEmail(manualEmail);
      sessionStorage.setItem('pending_verification_email', manualEmail);
      setShowEmailInput(false);
    }

    try {
      const result = await authService.resendVerificationEmail({
        email: resendEmail,
        emailRedirectTo: `${window.location.origin}/auth/verify-email`
      });

      if (result.success === false) {
        toast({
          title: "Error",
          description: result.error.userMessage,
          variant: "destructive"
        });
        
        Sentry.captureException(new Error(result.error.message), {
          tags: { context: 'resend_verification_error', code: result.error.code },
          extra: { email: resendEmail, error: result.error }
        });
        return;
      }

      toast({
        title: "Verification email sent",
        description: "Check your email for a new verification link."
      });
    } catch (error) {
      if (error instanceof ApiError) {
        toast({
          title: "Resend Failed",
          description: "Unable to resend verification email. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'api_error', operation: 'resend_verification' },
          extra: { message: error.message, email: resendEmail }
        });
      } else if (error instanceof NetworkError) {
        toast({
          title: "Connection Error",
          description: "Check your internet connection and try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'network_error', operation: 'resend_verification' },
          extra: { message: error.message }
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to resend verification email. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'unknown_error', operation: 'resend_verification' },
          extra: { message: 'Unknown resend error' }
        });
      }
    }
  };

  /** Fallback UI: manual email input when email cannot be resolved */
  const renderEmailFallback = () => (
    <div className="space-y-3">
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          We need your email address to resend the verification link.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="fallback-email" className="text-sm font-medium">Email address</Label>
        <Input
          id="fallback-email"
          type="email"
          placeholder="you@example.com"
          value={manualEmail}
          onChange={(e) => setManualEmail(e.target.value)}
          className="w-full"
        />
      </div>
      <CooldownButton
        onClick={handleResendVerification}
        cooldownSeconds={60}
        className="w-full"
        disabled={!manualEmail}
      >
        Resend verification email
      </CooldownButton>
    </div>
  );

  const renderResendSection = () => {
    if (showEmailInput && !email) {
      return renderEmailFallback();
    }

    return (
      <div className="space-y-3">
        <div className="text-center text-sm text-muted-foreground">Didn't receive it?</div>
        <CooldownButton 
          onClick={handleResendVerification} 
          cooldownSeconds={60} 
          variant="secondary" 
          className="w-full"
        >
          Resend verification email
        </CooldownButton>
        <Button onClick={() => navigate('/register')} variant="ghost" className="w-full text-sm">
          Change email address
        </Button>
      </div>
    );
  };

  const renderVerificationState = () => {
    switch (verificationStatus) {
      case 'waiting':
        return (
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              
              <div>
                <CardTitle className="text-2xl font-semibold mb-2">Check your inbox!</CardTitle>
                <p className="text-muted-foreground text-sm">We've sent a verification link to</p>
                {email ? (
                  <p className="font-medium text-base mt-1">{email}</p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1 italic">your registered email</p>
                )}
              </div>

              <div className="text-left space-y-2 pt-4">
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Click the link in the email to verify your account</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>This usually takes less than a minute</span>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {renderResendSection()}
            </CardContent>
          </Card>
        );

      case 'loading':
        return (
          <Card className="max-w-md mx-auto">
            <CardContent className="py-12 text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <h2 className="text-xl font-semibold">Verifying your email...</h2>
              <p className="text-sm text-muted-foreground">This usually takes less than 5 seconds</p>
            </CardContent>
          </Card>
        );

      case 'success':
        return (
          <Card className="max-w-md mx-auto">
            <CardContent className="py-12 text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-green-600">✅ You're verified!</h2>
              <p className="text-muted-foreground">Let's personalize your experience...</p>
            </CardContent>
          </Card>
        );

      case 'alreadyVerified':
        return (
          <Card className="max-w-md mx-auto">
            <CardContent className="py-12 text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold">Already verified!</h2>
              <p className="text-muted-foreground">Your email has already been verified. Redirecting...</p>
            </CardContent>
          </Card>
        );

      case 'expired':
        return (
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-semibold mb-2">Link expired</CardTitle>
                <p className="text-muted-foreground">{errorMessage}</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showEmailInput && !email ? renderEmailFallback() : (
                <>
                  <CooldownButton 
                    onClick={handleResendVerification} 
                    cooldownSeconds={60}
                    className="w-full"
                  >
                    Send new verification email
                  </CooldownButton>
                  <Button onClick={() => navigate('/register')} variant="outline" className="w-full">
                    Back to registration
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        );

      case 'invalidToken':
        return (
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-semibold mb-2">Invalid verification link</CardTitle>
                <p className="text-muted-foreground">
                  {errorMessage || 'This verification link is invalid. Please request a new one.'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showEmailInput && !email ? renderEmailFallback() : (
                <>
                  <CooldownButton 
                    onClick={handleResendVerification}
                    cooldownSeconds={60}
                    className="w-full"
                  >
                    Send new verification email
                  </CooldownButton>
                  <Button onClick={() => navigate('/register')} variant="outline" className="w-full">
                    Back to registration
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        );

      case 'error':
        return (
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-semibold mb-2">Something went wrong</CardTitle>
                <p className="text-muted-foreground">
                  {errorMessage || 'We encountered an error while verifying your email. Please try again.'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showEmailInput && !email ? renderEmailFallback() : (
                <>
                  <CooldownButton 
                    onClick={handleResendVerification}
                    cooldownSeconds={60}
                    className="w-full"
                  >
                    Send new verification email
                  </CooldownButton>
                  <Button onClick={() => navigate('/register')} variant="outline" className="w-full">
                    Back to registration
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        );

      default:
        return (
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              
              <div>
                <CardTitle className="text-2xl font-semibold mb-2">Check your email</CardTitle>
                <p className="text-muted-foreground text-sm">We sent a verification link to</p>
                {email ? (
                  <p className="font-medium text-base mt-1">{email}</p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1 italic">your registered email</p>
                )}
              </div>

              <div className="text-left space-y-2 pt-4">
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Click the link in the email to verify your account</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>This usually takes less than a minute</span>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {renderResendSection()}
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full">
        <ProgressSteps currentStep={verificationStatus === 'success' ? 3 : 2} />
        <div className="flex justify-center">
          <div className="max-w-md w-full">
            {renderVerificationState()}
          </div>
        </div>
      </div>
    </div>
  );
}